<template>
  <div class="dashboard-page">
  <v-container class="dashboard-container">
    <v-layout row>
      <v-flex xs12 sm10 md8 offset-sm1 offset-md2>

        <!-- Loading -->
        <v-progress-circular v-if="loading" indeterminate color="primary" class="mt-5"></v-progress-circular>

        <!-- Login Gate (not authenticated) -->
        <div v-else-if="!dancerIdentified" class="dashboard">
          <h2 class="dash-heading">IABF dashboard</h2>

          <!-- Step 1: Enter Email -->
          <div v-if="loginStep === 'email'">
            <p>Enter the email we have on file to access your dashboard.</p>
            <form @submit.prevent="onCheckEmail">
              <v-text-field
                v-model="loginEmail"
                label="Email"
                type="email"
                required
              />
              <p v-if="loginError" class="error-text">{{ loginError }}</p>
              <v-btn
                class="primary"
                :disabled="!loginEmail || checkingEmail"
                :loading="checkingEmail"
                type="submit"
              >
                Continue
              </v-btn>
            </form>
          </div>

          <!-- Step 2a: Existing account — enter password -->
          <div v-else-if="loginStep === 'password'">
            <p>Welcome Back! Enter your IABF or Broche Ballet Online password to log in.</p>
            <form @submit.prevent="onSignIn">
              <v-text-field
                :value="loginEmail"
                label="Email"
                disabled
              />
              <v-text-field
                v-model="loginPassword"
                label="Password"
                type="password"
                required
              />
              <p v-if="loginError" class="error-text">{{ loginError }}</p>
              <v-btn
                class="primary"
                :disabled="!loginPassword"
                type="submit"
              >
                Log In
              </v-btn>
              <p class="forgot-link"><a href="#" @click.prevent="onResetPassword">Forgot your password?</a></p>
              <p v-if="resetSent" class="success-text">Password reset email sent. Check your inbox.</p>
            </form>
            <p class="toggle-link"><a href="#" @click.prevent="loginStep = 'email'; loginError = ''">Use a different email</a></p>
          </div>

          <!-- Step 2b: New account — set password -->
          <div v-else-if="loginStep === 'setPassword'">
            <p>Welcome! Set a password to access your dashboard.</p>
            <form @submit.prevent="onSignUp">
              <v-text-field
                :value="loginEmail"
                label="Email"
                disabled
              />
              <v-text-field
                v-model="loginPassword"
                label="Create Password"
                type="password"
                required
              />
              <v-text-field
                v-model="confirmPassword"
                label="Confirm Password"
                type="password"
                required
              />
              <p v-if="loginError" class="error-text">{{ loginError }}</p>
              <v-btn
                class="primary"
                :disabled="!loginPassword || !confirmPassword"
                type="submit"
              >
                Set Password &amp; Log In
              </v-btn>
            </form>
            <p class="toggle-link"><a href="#" @click.prevent="loginStep = 'email'; loginError = ''">Use a different email</a></p>
          </div>
        </div>

        <!-- Dashboard Content — Option B: Now + My festival -->
        <div v-else class="dashboard">

          <!-- Canceled account: deposit-on-file status (not full festival prep) -->
          <template v-if="isCanceledAccount">
            <header class="dash-hero">
              <p class="dash-kicker">IABF account</p>
              <h1 class="dash-title">Hi{{ firstName ? ', ' + firstName : '' }}</h1>
              <p class="subtitle">
                You’re not registered for the current festival, but we still have your account on file.
              </p>
            </header>

            <section class="dash-block iabf-card canceled-deposit-card">
              <h2 class="dash-heading">Deposit on file</h2>
              <p v-if="canceledDepositConsumed" class="canceled-deposit-body">
                Your held deposit has already been applied
                <span v-if="canceledDepositConsumedYear"> to IABF {{ canceledDepositConsumedYear }}</span>.
                If that doesn’t look right, email
                <a href="mailto:hello@iabf.dance">hello@iabf.dance</a>.
              </p>
              <template v-else>
                <p class="canceled-deposit-amount">
                  ${{ canceledDepositUsd }}
                </p>
                <p class="canceled-deposit-body">
                  From your IABF {{ festivalYearLabel }} registration (after canceling).
                  This deposit can be transferred forward when you register for a future festival.
                </p>
                <template v-if="registrationOpenForCanceled">
                  <router-link to="/register" class="iabf-btn iabf-btn--primary">
                    Register for IABF {{ registrationYear }}
                  </router-link>
                  <p class="canceled-deposit-hint">
                    Use this same email at registration — we’ll recognize the held deposit automatically.
                  </p>
                </template>
                <p v-else class="canceled-deposit-hint">
                  Registration for IABF {{ registrationYear }} opens {{ earlyAccessOpensLabel }}.
                  Your deposit will transfer when you register — we’ll recognize this email.
                </p>
              </template>
              <p class="canceled-deposit-footer">
                Questions?
                <a href="mailto:hello@iabf.dance">hello@iabf.dance</a>
              </p>
            </section>
            <section v-if="hasPastFestivalYears" class="dash-block alumni-history">
              <h2 class="dash-heading">Festival history</h2>
              <festival-history />
            </section>
          </template>

          <!-- Prior-year enrollment only: register for next festival + history -->
          <template v-else-if="isPriorFestivalYear">
            <header class="dash-hero prior-hero">
              <p class="dash-kicker">IABF {{ festivalYearLabel }}</p>
              <h1 class="dash-title">{{ welcomeTitle }}</h1>
              <template v-if="registrationIsOpen">
                <p class="subtitle">
                  IABF {{ registrationYear }} is open for returning dancers. We'd love to see you there!
                </p>
                <router-link to="/register" class="history-cta">
                  Register for IABF {{ registrationYear }}
                </router-link>
              </template>
              <p v-else class="subtitle">
                Registration for IABF {{ registrationYear }} opens {{ earlyAccessOpensLabel }}.
              </p>
            </header>
            <section v-if="hasPastFestivalYears" class="dash-block alumni-history">
              <h2 class="dash-heading">Festival history</h2>
              <festival-history />
            </section>
          </template>

          <template v-else>
          <header class="dash-hero">
            <p class="dash-kicker">IABF {{ festivalYearLabel }}</p>
            <h1 class="dash-title">{{ welcomeTitle }}</h1>
            <p class="subtitle">{{ isFaculty ? "We're so glad you're teaching with us. A few things help us take care of you this week." : "We're so glad you're here. Let's make this a smooth, joyful festival." }}</p>
          </header>

          <a
            v-if="guidebookUrl"
            class="resource-card resource-card--wide is-link guidebook-card"
            :href="guidebookUrl"
            target="_blank"
            rel="noopener"
          >
            <div class="resource-icon">📖</div>
            <div>
              <div class="resource-name">Festival guidebook</div>
              <div class="resource-sub">Your week at IABF — packing, campus, meals, and what to expect</div>
            </div>
          </a>
          <div
            v-else
            class="resource-card resource-card--wide guidebook-card"
          >
            <div class="resource-icon">📖</div>
            <div>
              <div class="resource-name">Festival guidebook</div>
              <div class="resource-sub">Your week at IABF — packing, campus, meals, and what to expect</div>
            </div>
          </div>

          <!-- ═══ NOW — only time-sensitive (deadlines / money / soon rehearsals) ═══ -->
          <section v-if="hasNowSection" class="dash-block now-block">
            <div class="section-head">
              <h2 class="dash-heading">Now</h2>
              <p class="section-lede">Just the things that need a little love soon</p>
            </div>

            <div v-if="totalOutstandingBalance > 0" class="iabf-banner iabf-banner--danger costume-balance-banner">
              <div class="costume-balance-text">
                <strong>Outstanding balance: ${{ totalOutstandingBalance }}</strong>
                <span v-if="costumeBalanceDue > 0 && guestBalanceDue > 0">
                  Includes costume (${{ costumeBalanceDue }}) and guests (${{ guestBalanceDue }}).
                </span>
                <span v-else-if="costumeBalanceDue > 0">Costume balance.</span>
                <span v-else>Guest registration balance.</span>
              </div>
              <button
                type="button"
                class="iabf-btn iabf-btn--primary costume-balance-btn"
                :disabled="combinedPaymentLoading"
                @click="payOutstandingBalance"
              >
                {{ combinedPaymentLoading ? 'Loading…' : 'Pay now' }}
              </button>
            </div>

            <div v-if="showRehearsalInNow" class="now-rehearsal iabf-card">
              <p class="now-rehearsal-kicker">Next rehearsal</p>
              <p class="now-rehearsal-day">{{ nextRehearsalDay.dayLabel }}</p>
              <div v-for="block in nextRehearsalDay.blocks" :key="block.pieceKey" class="now-rehearsal-row">
                <span>{{ block.pieceName }}</span>
                <span class="muted">{{ block.displayTime }}</span>
                <a :href="zoomLink" target="_blank" rel="noopener" class="rehearsal-join">Join Zoom →</a>
              </div>
            </div>

            <div v-if="nowItems.length" class="now-list">
              <button
                v-for="item in nowItems"
                :key="item.id"
                type="button"
                class="now-item"
                @click="onFestivalCardClick(item)"
              >
                <span class="now-item-dot" />
                <span class="now-item-text">
                  <strong>{{ item.title }}</strong>
                  <span>{{ item.nowHint || item.summary }}</span>
                </span>
                <span class="checklist-chev">›</span>
              </button>
            </div>
          </section>

          <!-- No empty "clear for now" card — Now section is omitted when quiet -->

          <!-- ═══ MY FESTIVAL (cards only — welcome header is enough) ═══ -->
          <section class="dash-block festival-block">
            <div class="festival-grid">
              <article
                v-for="card in festivalCards"
                :key="card.id"
                class="festival-card"
                :class="{
                  'is-attention': card.status === 'attention',
                  'is-ready': card.status === 'ready',
                  'is-muted': card.status === 'soon' || card.placeholder,
                  'is-placeholder': card.placeholder
                }"
                :role="card.placeholder ? undefined : 'button'"
                :tabindex="card.placeholder ? undefined : 0"
                @click="onFestivalCardClick(card)"
                @keyup.enter="onFestivalCardClick(card)"
              >
                <div class="festival-card-top">
                  <span class="festival-icon">{{ card.icon }}</span>
                  <span class="status-chip" :class="'chip-' + card.status">{{ card.statusLabel }}</span>
                </div>
                <h3 class="festival-card-title">{{ card.title }}</h3>
                <p class="festival-card-summary">{{ card.summary }}</p>
                <span v-if="card.placeholder" class="festival-card-cta is-placeholder-cta">{{ card.cta }}</span>
                <span v-else class="festival-card-cta">{{ card.cta }} ›</span>
              </article>
            </div>
          </section>

          <!-- ═══ COMMUNITY ═══ -->
          <section class="dash-block resources-section">
            <div class="section-head">
              <h2 class="dash-heading">Community</h2>
              <p class="section-lede">Connect with other IABF dancers.</p>
            </div>
            <div class="artie-host-card">
              <button
                type="button"
                class="artie-host-photo"
                aria-label="See Artie larger"
                @click="artieLightbox = true"
              >
                <img src="/brand/artie-armadillo.jpg" alt="Artie the armadillo">
              </button>
              <div>
                <h1 class="dash-title">
                  Hi, I’m Artie the Armadillo
                  <span class="artie-mascot-badge">Mascot</span>
                </h1>
                <p class="subtitle">
                  I’m the IABF {{ festivalYearLabel }} Mascot. You can find me wandering the nature preserve at ACA and cheering you on! Look closely at my tattoos and you’ll see all kinds of history from {{ artieHistoryYear }}.
                </p>
              </div>
            </div>
            <div class="resources-stack">
              <router-link to="/community" class="resource-card resource-card--wide is-link">
                <div class="resource-icon">👋</div>
                <div>
                  <div class="resource-name">IABF intros</div>
                  <div class="resource-sub">Headshots, hometowns, and a hello — meet this year’s IABF-ers, or add your own intro</div>
                </div>
              </router-link>
            <a
              class="resource-card resource-card--wide is-link"
              :href="communityForumUrl"
              target="_blank"
              rel="noopener"
            >
              <div class="resource-icon">💬</div>
              <div>
                <div class="resource-name">Community forum</div>
                <div class="resource-sub">Meet other dancers, ask questions, share excitement</div>
              </div>
            </a>
            <a
              v-if="whatsappInviteUrl"
              class="resource-card resource-card--wide is-link"
              :href="whatsappInviteUrl"
              target="_blank"
              rel="noopener"
            >
              <div class="resource-icon">📱</div>
              <div>
                <div class="resource-name">WhatsApp group</div>
                <div class="resource-sub">Stay in touch the week of IABF</div>
              </div>
            </a>
            <div
              v-else
              class="resource-card resource-card--wide resource-soon"
            >
              <div class="resource-icon">📱</div>
              <div>
                <div class="resource-name">WhatsApp group · Coming July 2027</div>
                <div class="resource-sub">Stay in touch the week of IABF</div>
              </div>
            </div>
            </div>
          </section>

          <!-- ═══ RESOURCES ═══ -->
          <section v-if="showResourcesSection" class="dash-block resources-section">
            <div class="section-head">
              <h2 class="dash-heading">Resources</h2>
              <p class="section-lede">Guides to help you prep — open any time.</p>
            </div>

            <div class="resources-stack">
              <!-- Stage makeup tutorial (expandable) -->
              <div v-if="!isFaculty" id="stage-makeup" class="makeup-block">
                <button type="button" class="makeup-toggle" @click="showMakeup = !showMakeup">
                  <span>
                    <strong>Stage makeup tutorial</strong>
                    <span class="makeup-toggle-sub">Videos · tips · shop discount</span>
                  </span>
                  <span class="chev">{{ showMakeup ? '▴' : '▾' }}</span>
                </button>
                <div v-if="showMakeup" class="makeup-body">
                  <p>
                    Keep makeup simple if you like — or learn full stage technique with Suzanne Patterson
                    (Emmy Award–winning) and Veronica K.
                  </p>
                  <h3 class="makeup-h">Overview</h3>
                  <div class="video-embed">
                    <iframe src="https://cdn.jwplayer.com/players/98ymsZyy-ZbyADQFv.html" width="100%" height="360" frameborder="0" allowfullscreen></iframe>
                  </div>
                  <h3 class="makeup-h">How to apply</h3>
                  <div class="video-embed">
                    <iframe src="https://cdn.jwplayer.com/players/awwbDGoa-ZbyADQFv.html" width="100%" height="360" frameborder="0" allowfullscreen></iframe>
                  </div>
                  <p>Code <strong>BROCHEIABF</strong> — 20% off + free shipping on orders over $50.</p>
                  <a
                    href="https://www.veronicakballet.com/stage-makeup"
                    target="_blank"
                    rel="noopener"
                    class="iabf-btn iabf-btn--secondary"
                  >
                    Shop stage makeup
                  </a>
                </div>
              </div>

              <div class="resources-grid guide-grid">
                <a
                  v-for="item in resourceLinks"
                  :key="item.id"
                  class="resource-card is-link"
                  :href="item.href"
                  target="_blank"
                  rel="noopener"
                >
                  <div class="resource-icon">{{ item.icon }}</div>
                  <div class="resource-name">{{ item.title }}</div>
                  <div v-if="item.sub" class="resource-sub">{{ item.sub }}</div>
                </a>
              </div>
            </div>
          </section>

          <!-- ═══ COMING SOON ═══ -->
          <section v-if="guideSoon.length" class="dash-block resources-section">
            <p class="coming-soon-label">Coming soon for {{ festivalYearLabel }}</p>
            <div class="resources-grid guide-grid guide-soon">
              <div v-for="item in guideSoon" :key="item.id" class="resource-card resource-soon">
                <div class="resource-icon">{{ item.icon }}</div>
                <div class="resource-name">{{ item.title }}</div>
                <div class="resource-sub">{{ item.sub || 'Coming soon' }}</div>
              </div>
            </div>
          </section>

          <section v-if="hasPastFestivalYears" class="dash-block alumni-history">
            <h2 class="dash-heading">Festival history</h2>
            <festival-history />
          </section>
          </template>

          <footer class="dash-footer">
            <p>Questions? Email <a href="mailto:hello@iabf.dance">hello@iabf.dance</a></p>
            <button type="button" class="logout-btn" @click="onLogOut">Log out</button>
          </footer>

        </div>
      </v-flex>
    </v-layout>

    <v-dialog v-model="showStoreOrdersDialog" max-width="560">
      <v-card class="store-orders-card">
        <v-card-title class="store-orders-title">Your IABF merch order{{ storeOrders.length === 1 ? '' : 's' }}</v-card-title>
        <v-card-text class="store-orders-body">
          <div v-for="o in storeOrders" :key="o.id" class="store-order-block">
            <ul class="store-order-lines">
              <li v-for="(l, li) in (o.lines || [])" :key="li">
                {{ l.quantity }}× {{ l.name }}<span v-if="l.size"> · {{ l.size }}</span>
              </li>
            </ul>
            <div class="store-order-meta">
              <span>Total paid: <strong>${{ ((o.totalCents || 0) / 100).toFixed(2) }}</strong></span>
            </div>
          </div>
          <p class="store-orders-hint">Your order will be ready for pickup at IABF check-in.</p>
        </v-card-text>
        <v-card-actions class="store-orders-actions">
          <v-btn text @click="showStoreOrdersDialog = false">Close</v-btn>
          <v-spacer />
          <v-btn
            v-if="storeStillOpen()"
            color="#dd4762"
            dark
            to="/store"
          >Add to my order →</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
  <div
    v-if="artieLightbox"
    class="artie-lightbox"
    role="dialog"
    aria-modal="true"
    aria-label="Artie the Armadillo"
    @click.self="artieLightbox = false"
    @keydown.esc.prevent="artieLightbox = false"
  >
    <button type="button" class="artie-lightbox-close" aria-label="Close" @click="artieLightbox = false">×</button>
    <img src="/brand/artie-armadillo.jpg" alt="Artie the Armadillo — IABF mascot">
    <p class="artie-lightbox-caption">Look closely — the shell is full of IABF {{ artieHistoryYear }} history.</p>
  </div>
  </div>
</template>

<script>
import { auth } from '../../firebase'
import { google, outlook, yahoo, ics } from 'calendar-link'
import { rehearsalsForYear } from '@/utils/iabf-rehearsals'
import { IABF } from '@/config/iabf-collections'
import { IABF_DANCER_YEAR, IABF_REGISTRATION_YEAR, hasCorpsCostumeOrder } from '@/config/iabf-event'
import { hasPastFestivalYears as dancerHasPastFestivalYears } from '@/utils/iabf-festival-history'
import { yearsAttendedForDancer } from '@/utils/iabf-attendance-years'
import { festivalWeek } from '@/config/iabf-festival-dates'
import { EARLY_ACCESS_OPENS_LABEL } from '@/config/iabf-registration-2027'
import { IABF_COMMUNITY_FORUM_URL } from '@/config/iabf-intro'
import {
  giftsFromDancer,
  hasTripDetailsOnFile,
  lodgingLabelFromRoom,
  lodgingRequiredPrepDone,
  registrationSummaryLine
} from '@/utils/iabf-registration-package'
import { FACULTY_GUIDE_IDS, isFacultyAccount } from '@/utils/iabf-faculty'
import { IABF_GUIDEBOOK_PDF_URL, IABF_POSING_GUIDE_PDF_URL } from '@/config/iabf-resources'
import { canSeeDancerPhotoshootSchedule } from '@/config/iabf-photoshoot'
import { ordinalYearLabel } from '@/utils/iabf-people'
import FestivalHistory from '@/components/iabf/FestivalHistory.vue'

const ZOOM_LINK = 'https://us02web.zoom.us/j/2227683736?pwd=eXVFTWZSdHZZVTF0V1laYzQyVEdCdz09'

// All rehearsal dates fall between May 10 and June 21, 2026 — entirely within
// US Eastern Daylight Time (UTC-4), so we hardcode the offset when building
// Date objects. Times are 24-hour ET.
const ET_OFFSET = '-04:00'

function etDate (dateStr, timeStr) {
  return new Date(`${dateStr}T${timeStr}:00${ET_OFFSET}`)
}

function formatTime (date) {
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })
}

function getZoneAbbrev (date) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { hour: 'numeric', timeZoneName: 'short' }).formatToParts(date)
    const zonePart = parts.find(p => p.type === 'timeZoneName')
    return zonePart ? zonePart.value : ''
  } catch (e) {
    return ''
  }
}

function formatTimeRange (startDate, endDate, includeZone = true) {
  const startStr = formatTime(startDate)
  const endStr = formatTime(endDate)
  const startPeriod = startStr.match(/(AM|PM)$/i)
  const endPeriod = endStr.match(/(AM|PM)$/i)
  const startDisplay = (startPeriod && endPeriod && startPeriod[0].toUpperCase() === endPeriod[0].toUpperCase())
    ? startStr.replace(/\s?(AM|PM)$/i, '')
    : startStr
  const zone = includeZone ? getZoneAbbrev(endDate) : ''
  return zone ? `${startDisplay} – ${endStr} ${zone}` : `${startDisplay} – ${endStr}`
}

function formatDayLabel (date) {
  return date.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })
}

export default {
  components: { FestivalHistory },
  data () {
    return {
      loginEmail: '',
      loginPassword: '',
      confirmPassword: '',
      loginError: '',
      loginStep: 'email',
      checkingEmail: false,
      resetSent: false,
      combinedPaymentLoading: false,
      cardUpdateLoading: false,
      cardUpdateSuccess: false,
      showDoneTodos: false,
      showMakeup: false,
      // Mirrors iabf-config-2026/itinerary.published — gates the "Class
      // Locations" tile in the Festival prep grid so the link only
      // appears once the schedule is published to dancers.
      itineraryPublished: false,
      showCancellationPolicy: false,
      storeHasItems: false,
      storeOrders: [],
      storeOrderDeadline: '',
      showStoreOrdersDialog: false,
      rehearsalRecordings: {},
      rehearsalSessions: [],
      // 2027 registration gate (canceled deposit CTA). Default hidden until loaded.
      registrationAccess: null,
      communityForumUrl: IABF_COMMUNITY_FORUM_URL,
      artieLightbox: false,
      photoshootBookings: [],
      photoshootDancerBookingOpen: false,
      groupPiecesForCard: []
    }
  },
  computed: {
    loading () {
      return this.$store.getters.loading
    },
    currentIABFDancer () {
      return this.$store.getters.currentIABFDancer
    },
    dancer () {
      return this.currentIABFDancer || {}
    },
    pieces () {
      return this.$store.getters.loadedIABFPiecesforDancer
    },
    user () {
      return this.$store.getters.user
    },
    dancerIdentified () {
      return this.currentIABFDancer && this.currentIABFDancer.id
    },
    carpoolHasMembership () {
      const id = this.dancer && this.dancer.id
      if (!id) return false
      const slots = this.$store.getters.iabfCarpoolSlots || []
      return slots.some(s => !s.isWaitlist && (s.dancers || []).some(d => d.id === id))
    },
    firstName () {
      return this.currentIABFDancer ? this.currentIABFDancer.firstName : ''
    },
    hasPastFestivalYears () {
      const person = this.$store.getters.currentIABFPerson || {}
      const d = this.currentIABFDancer || {}
      return dancerHasPastFestivalYears({
        yearsAttended: yearsAttendedForDancer(person, d, this.enrollmentYear),
        enrollmentYear: d.festivalYear || this.enrollmentYear,
        isCanceledAccount: !!d.isCanceledAccount,
        canceledYears: d.canceledYears
      })
    },
    attendanceYearNumber () {
      const person = this.$store.getters.currentIABFPerson || {}
      const d = this.currentIABFDancer || {}
      return yearsAttendedForDancer(person, d, this.enrollmentYear).length
    },
    welcomeTitle () {
      const name = this.firstName ? ', ' + this.firstName : ''
      // Ordinal is “you’re here for year N of this festival.” Alumni who
      // haven’t registered for this year just get a warm welcome back.
      if (this.isPriorFestivalYear) {
        return 'Welcome back' + name
      }
      const n = this.attendanceYearNumber
      if (n <= 1) {
        return 'Welcome to IABF' + name
      }
      const nth = ordinalYearLabel(n)
      return 'Welcome back for the ' + nth + ' Year' + name
    },
    zoomLink () {
      return ZOOM_LINK
    },
    whatsappInviteUrl () {
      return this.$store.getters.iabfWhatsappInviteUrl || ''
    },
    formattedRehearsalDays () {
      const dancer = this.currentIABFDancer || {}
      const year = Number(dancer.festivalYear) || this.enrollmentYear || IABF_DANCER_YEAR
      return rehearsalsForYear(year, this.rehearsalSessions)
        .map(day => {
          const blocks = day.blocks.filter(b => dancer[b.pieceKey] && dancer[b.pieceKey].participating)
          if (blocks.length === 0) return null
          const firstStart = etDate(day.date, blocks[0].start)
          const lastEnd = etDate(day.date, blocks[blocks.length - 1].end)
          return {
            date: day.date,
            dayLabel: formatDayLabel(firstStart),
            lastEnd,
            blocks: blocks.map(block => {
              const startDate = etDate(day.date, block.start)
              const endDate = etDate(day.date, block.end)
              return {
                pieceKey: block.pieceKey,
                pieceName: block.pieceName,
                start: startDate,
                end: endDate,
                displayTime: formatTimeRange(startDate, endDate),
                recording: this.rehearsalRecordings[`${day.date}:${block.pieceKey}`] || block.recording || null,
                splits: (block.splits || []).map(s => ({
                  label: s.label,
                  start: s.start,
                  end: s.end,
                  displayTime: formatTimeRange(etDate(day.date, s.start), etDate(day.date, s.end), false)
                }))
              }
            })
          }
        })
        .filter(Boolean)
    },
    upcomingRehearsals () {
      const now = new Date()
      return this.formattedRehearsalDays.filter(day => day.lastEnd >= now)
    },
    pastRehearsals () {
      const now = new Date()
      return this.formattedRehearsalDays
        .filter(day => day.lastEnd < now)
        .slice()
        .reverse()
    },
    nextRehearsalDay () {
      return this.upcomingRehearsals[0] || null
    },
    laterRehearsalDays () {
      return this.upcomingRehearsals.slice(1)
    },
    costumeBalanceDue () {
      const d = this.dancer
      const cost = Number(d.costumeTotalCost) || 0
      if (d.costumeOrderCompleted && !d.costumeOrderPaid && cost > 0) {
        return cost
      }
      return 0
    },
    guestBalanceDue () {
      return this.$store.getters.guestRegistrations
        .filter(r => r.paymentStatus === 'unpaid' && Number(r.totalCost) > 0)
        .reduce((sum, r) => sum + (Number(r.totalCost) || 0), 0)
    },
    unpaidGuestIds () {
      return this.$store.getters.guestRegistrations
        .filter(r => r.paymentStatus === 'unpaid' && r.totalCost > 0)
        .map(r => r.id)
    },
    totalOutstandingBalance () {
      return this.costumeBalanceDue + this.guestBalanceDue
    },
    // Includes both unpaid and added_to_next guests — used for inline display
    // on the final scheduled payment so dancers see what's been scheduled in.
    // Keep separate from totalOutstandingBalance to avoid the Pay Now banner
    // double-charging guests that are already bundled into a scheduled payment.
    finalPaymentGuestBalance () {
      return this.$store.getters.guestRegistrations
        .filter(r => r.paymentStatus !== 'paid' && Number(r.totalCost) > 0)
        .reduce((sum, r) => sum + (Number(r.totalCost) || 0), 0)
    },
    finalPaymentExtraBalance () {
      return this.costumeBalanceDue + this.finalPaymentGuestBalance
    },
    finalPaymentBreakdownLabel () {
      const parts = []
      if (this.finalPaymentGuestBalance > 0) parts.push(`$${this.finalPaymentGuestBalance} for your registered guests`)
      if (this.costumeBalanceDue > 0) parts.push(`$${this.costumeBalanceDue} for your costume order`)
      return parts.length ? `+ ${parts.join(' + ')}` : ''
    },
    /**
     * True only when every scheduled installment with a due amount is Paid/N/A
     * and there is no outstanding costume/guest balance.
     * Missing status on a due installment = unpaid (do not treat empty as paid).
     */
    isFullyPaid () {
      const d = this.dancer || {}
      if (this.totalOutstandingBalance > 0) {
        return false
      }
      let hasDueInstallment = false
      for (let n = 1; n <= 6; n++) {
        const due = Number(d[`payment${n}Due`]) || 0
        if (due <= 0) {
          continue
        }
        hasDueInstallment = true
        const status = d[`payment${n}Status`]
        if (status !== 'Paid' && status !== 'N/A') {
          return false
        }
      }
      if (hasDueInstallment) {
        return true
      }
      // No installment schedule: paid only if a non-zero deposit/initial is on file
      // or an explicit paid flag — not merely because statuses are empty.
      if (Number(d.initialPayment) > 0) {
        return true
      }
      return false
    },
    /**
     * Next scheduled auto-charge (installments). Null if fully paid or no schedule.
     * Labels match Billing page month schedule (5th of month).
     */
    nextPayment () {
      const d = this.dancer || {}
      if (this.isFullyPaid) {
        return null
      }
      const monthLabels = {
        1: 'Jan 5',
        2: 'Feb 5',
        3: 'Mar 5',
        4: 'Apr 5',
        5: 'May 5',
        6: 'Jun 5'
      }
      for (let n = 1; n <= 6; n++) {
        const due = Number(d[`payment${n}Due`]) || 0
        if (due <= 0) {
          continue
        }
        const status = d[`payment${n}Status`]
        if (status === 'Paid' || status === 'N/A') {
          continue
        }
        return {
          n,
          amount: due,
          dateLabel: monthLabels[n] || ('Payment ' + n),
          overdue: status === 'Overdue'
        }
      }
      return null
    },
    /** Effective enrollment year (admin masquerade uses the admin year switcher). */
    enrollmentYear () {
      const dancerYear = Number(
        (this.currentIABFDancer && this.currentIABFDancer.festivalYear) ||
        this.$store.getters.iabfDancerYear ||
        0
      )
      if (this.$store.state.masqueradeDancerId) {
        if (Number.isFinite(dancerYear) && dancerYear > 0) {
          return dancerYear
        }
        return Number(this.$store.getters.adminFestivalYear) || IABF_DANCER_YEAR
      }
      return Number.isFinite(dancerYear) && dancerYear > 0
        ? dancerYear
        : IABF_DANCER_YEAR
    },
    festivalYearLabel () {
      return String(this.enrollmentYear)
    },
    artieHistoryYear () {
      const y = Number(this.enrollmentYear)
      return Number.isFinite(y) && y > 1 ? String(y - 1) : '2026'
    },
    registrationYear () {
      return IABF_REGISTRATION_YEAR
    },
    /**
     * True when this dancer's enrollment is for a past festival year
     * (e.g. 2026 only — not registered for the current dancer year).
     * Full prep UI is hidden; they only see welcome + showcase recordings.
     */
    isPriorFestivalYear () {
      if (this.isCanceledAccount) {
        return false
      }
      const y = this.enrollmentYear
      return Number.isFinite(y) && y > 0 && y < IABF_DANCER_YEAR
    },
    /** Canceled registration with held deposit — simplified dashboard. */
    isCanceledAccount () {
      return !!(this.dancer && this.dancer.isCanceledAccount)
    },
    isFaculty () {
      return isFacultyAccount(this.dancer)
    },
    guidebookUrl () {
      return IABF_GUIDEBOOK_PDF_URL || ''
    },
    canSeePhotoshootSchedule () {
      const email = this.user && this.user.email
      return canSeeDancerPhotoshootSchedule(email, this.photoshootDancerBookingOpen)
    },
    facultyFestivalCards () {
      const d = this.dancer || {}
      const travelDoc = this.$store.getters.loadedTravelDetails || {}
      const travelDone = lodgingRequiredPrepDone(d, travelDoc)
      const dietDone = !!d.dietaryRestrictionsEntered
      const guests = this.$store.getters.guestRegistrations || []
      const guestCount = guests.length
      const complimentaryUsed = guests.some(g => g.facultyComplimentary === true)
      const softOpen = (done, doneLabel = 'Saved') => {
        if (done) {
          return { status: 'ready', statusLabel: doneLabel }
        }
        return { status: 'soon', statusLabel: 'To-do' }
      }
      const travelChip = softOpen(travelDone, 'All set')
      const dietChip = softOpen(dietDone, 'All set')
      return [
        {
          id: 'travel',
          icon: '✈️',
          title: 'Getting here',
          summary: travelDone
            ? this.travelSummaryLine
            : 'Share your flights or drive times when you know them',
          status: travelChip.status,
          statusLabel: travelChip.statusLabel,
          cta: travelDone ? 'View or update' : 'Add trip details',
          to: '/lodging',
          urgent: false,
          nowHint: null
        },
        {
          id: 'dietary',
          icon: '🥗',
          title: 'How you eat',
          summary: dietDone ? this.dietarySummaryLine : 'So festival meals feel good for you',
          status: dietChip.status,
          statusLabel: dietChip.statusLabel,
          cta: dietDone ? 'View or update' : 'Tell us how you eat',
          to: '/dietary',
          urgent: false,
          nowHint: null
        },
        {
          id: 'guests',
          icon: '🎟️',
          title: 'Your guest',
          summary: guestCount
            ? (guestCount === 1 ? '1 guest on meals' : guestCount + ' guests')
            : 'One guest is included for festival meals',
          status: complimentaryUsed || guestCount ? 'ready' : 'soon',
          statusLabel: complimentaryUsed || guestCount ? 'Saved' : 'Included',
          cta: guestCount ? 'View or add' : 'Add your guest',
          to: '/guests',
          urgent: false,
          nowHint: null
        },
        (() => {
          const name = [d.firstName, d.lastName].filter(Boolean).join(' ').trim()
          const email = String(d.email || '').toLowerCase()
          const leading = (this.groupPiecesForCard || []).filter(p =>
            (p.facultyId && p.facultyId === d.id) ||
            (p.facultyEmail && p.facultyEmail === email) ||
            (name && p.facultyName === name)
          )
          if (!leading.length) {
            return null
          }
          return {
            id: 'group-rehearsals',
            icon: '🩰',
            title: 'Group rehearsals',
            summary: leading.length === 1
              ? 'You’re leading ' + leading[0].title
              : 'You’re leading ' + leading.length + ' group pieces',
            status: 'ready',
            statusLabel: 'On the schedule',
            cta: 'Your itinerary',
            to: name
              ? { path: '/iabf/teacher-itinerary', query: { teacher: name } }
              : '/itinerary',
            urgent: false,
            nowHint: null
          }
        })(),
        {
          id: 'photoshoot-edits',
          icon: '📸',
          title: 'Photoshoot edits',
          summary: 'If you’re editing festival photos, your queue lives here',
          status: 'soon',
          statusLabel: 'Queue',
          cta: 'Open queue',
          to: '/photoshoot',
          urgent: false,
          nowHint: null
        }
      ].filter(Boolean)
    },
    canceledDepositUsd () {
      const cents = Number(this.dancer && this.dancer.depositOnFileCents) || 0
      return (cents / 100).toFixed(0)
    },
    canceledDepositConsumed () {
      return !!(this.dancer && this.dancer.depositCreditConsumed)
    },
    canceledDepositConsumedYear () {
      return (this.dancer && this.dancer.depositCreditConsumedYear) || null
    },
    earlyAccessOpensLabel () {
      return EARLY_ACCESS_OPENS_LABEL
    },
    /** Register CTA — only once early or public registration is actually open. */
    registrationIsOpen () {
      const mode = this.registrationAccess && this.registrationAccess.mode
      return mode === 'public' || mode === 'early'
    },
    /** Canceled-deposit Register CTA — only once early or public registration is actually open. */
    registrationOpenForCanceled () {
      return this.registrationIsOpen
    },
    /**
     * Deadlines that can make an incomplete item urgent in "Now".
     * Use ISO dates (YYYY-MM-DD). null = never surfaces in Now for incompleteness alone.
     * Carpool is intentionally never urgent (opens much closer to the festival).
     */
    prepDeadlines () {
      return {
        // Set these when 2027 windows open, e.g. dietary: '2027-06-01'
        dietary: null,
        travel: null,
        emergency: null,
        music: null,
        costume: null,
        carpool: null
      }
    },
    /** Permanent "My festival" cards — always visible for reference. */
    festivalCards () {
      if (this.isFaculty) {
        return this.visiblePhotoshootCards(this.facultyFestivalCards)
      }
      const d = this.dancer || {}
      const room = d.room || ''
      const travelDoc = this.$store.getters.loadedTravelDetails || {}
      const travelDone = lodgingRequiredPrepDone(d, travelDoc)
      const tripOnFile = hasTripDetailsOnFile(d, travelDoc)
      const dietDone = !!d.dietaryRestrictionsEntered
      const emergencyDone = !!d.emergencyContactEntered
      const pieceCount = (this.pieces && this.pieces.length) || 0
      const musicMissing = this.hasMissingMusic
      const guestCount = (this.$store.getters.guestRegistrations || []).length
      const inCorps = this.isInCorps
      const deadlines = this.prepDeadlines
      const person = this.$store.getters.currentIABFPerson || {}
      const dancerForGifts = {
        ...d,
        festivalYear: Number(d.festivalYear) || this.enrollmentYear || IABF_DANCER_YEAR,
        yearsAttended: yearsAttendedForDancer(person, d, this.enrollmentYear)
      }
      const giftInfo = giftsFromDancer(dancerForGifts)

      const travelSummary = travelDone
        ? this.travelSummaryLine
        : (tripOnFile
          ? 'Trip is in — still a couple of room details'
          : 'Share your flights or drive times when you know them')

      // Chip language:
      // - done → "Saved" / "All set" (ready)
      // - not done, no deadline → "To-do" (soft / soon chip — not urgency)
      // - not done + deadline soon → attention chip
      // - money owed → "Balance due" (attention)
      const softOpen = (done, doneLabel = 'Saved') => {
        if (done) {
          return { status: 'ready', statusLabel: doneLabel }
        }
        return { status: 'soon', statusLabel: 'To-do' }
      }
      const travelUrgent = !travelDone && this.isDeadlineUrgent(deadlines.travel)
      const dietUrgent = !dietDone && this.isDeadlineUrgent(deadlines.dietary)
      const emergencyUrgent = !emergencyDone && this.isDeadlineUrgent(deadlines.emergency)
      const musicUrgent = musicMissing && this.isDeadlineUrgent(deadlines.music)

      const travelChip = travelUrgent
        ? { status: 'attention', statusLabel: 'Due soon' }
        : softOpen(travelDone, 'All set')
      const dietChip = dietUrgent
        ? { status: 'attention', statusLabel: 'Due soon' }
        : softOpen(dietDone, 'All set')
      const emergencyChip = emergencyUrgent
        ? { status: 'attention', statusLabel: 'Due soon' }
        : softOpen(emergencyDone, 'All set')

      // status = My festival chip; urgent = only time-sensitive (drives Now strip)
      const cards = [
        {
          id: 'registration',
          icon: '🎫',
          title: 'You’re in!',
          summary: registrationSummaryLine(dancerForGifts),
          status: giftInfo.needsSelection ? 'attention' : 'ready',
          statusLabel: giftInfo.needsSelection
            ? (giftInfo.entitled > 1 ? 'Gifts to pick' : 'Gift to pick')
            : 'All set',
          cta: 'See what’s included',
          to: '/registration',
          urgent: false,
          nowHint: null
        },
        {
          id: 'travel',
          icon: '✈️',
          title: 'Getting here',
          summary: travelSummary,
          status: travelChip.status,
          statusLabel: travelChip.statusLabel,
          cta: travelDone ? 'View or update' : (tripOnFile ? 'Finish room details' : 'Add trip details'),
          to: '/lodging',
          urgent: travelUrgent,
          nowHint: travelUrgent
            ? this.deadlineNowHint('Trip details', deadlines.travel)
            : null
        },
        {
          id: 'dietary',
          icon: '🥗',
          title: 'How you eat',
          summary: dietDone ? this.dietarySummaryLine : 'So festival meals feel good for you',
          status: dietChip.status,
          statusLabel: dietChip.statusLabel,
          cta: dietDone ? 'View or update' : 'Tell us how you eat',
          to: '/dietary',
          urgent: dietUrgent,
          nowHint: dietUrgent
            ? this.deadlineNowHint('How you eat', deadlines.dietary)
            : null
        },
        {
          id: 'emergency',
          icon: '🆘',
          title: 'Who we should call',
          summary: emergencyDone
            ? `${d.emergencyContactName} (${d.emergencyContactRelationship})`
            : 'A person we can reach if we need to look after you',
          status: emergencyChip.status,
          statusLabel: emergencyChip.statusLabel,
          cta: emergencyDone ? 'View or update' : 'Add someone',
          to: '/profile',
          urgent: emergencyUrgent,
          nowHint: emergencyUrgent
            ? this.deadlineNowHint('Who we should call', deadlines.emergency)
            : null
        },
        (() => {
          const prep = d.showcasePrepStatus || null
          const notPerforming = prep === 'not_performing' && !pieceCount
          const piecesDone = prep === 'all_set' && pieceCount > 0 && !musicMissing
          // All set only after pieces are done AND competition is entered or declined
          // (or they opted out of solos entirely)
          const competitionDecided = !!(d.competitionRegistered || d.competitionDeclined)
          const allSet = notPerforming || (piecesDone && competitionDecided)
          let status = 'soon'
          let statusLabel = 'Optional'
          let summary = 'Solos are optional — add one, or mark not performing'
          let cta = 'Open showcase'
          if (musicMissing) {
            status = 'attention'
            statusLabel = 'Music needed'
            summary = `${pieceCount} piece${pieceCount === 1 ? '' : 's'} · still need music`
            cta = 'Upload music'
          } else if (notPerforming) {
            status = 'ready'
            statusLabel = 'All set'
            summary = 'No solos this year — not performing'
            cta = 'Change'
          } else if (allSet) {
            status = 'ready'
            statusLabel = 'All set'
            summary = `${pieceCount} piece${pieceCount === 1 ? '' : 's'} · music ready`
            if (d.competitionRegistered) {
              summary += ' · competition entered'
            } else if (d.competitionDeclined) {
              summary += ' · not competing'
            }
            cta = 'View pieces'
          } else if (piecesDone && !competitionDecided) {
            status = 'soon'
            statusLabel = 'In progress'
            summary = `${pieceCount} piece${pieceCount === 1 ? '' : 's'} ready · choose competition`
            cta = 'Decide on competition'
          } else if (pieceCount > 0) {
            status = 'soon'
            statusLabel = 'In progress'
            summary = `${pieceCount} piece${pieceCount === 1 ? '' : 's'} · music ready`
            if (d.competitionRegistered) {
              summary += ' · competition entered'
            } else if (d.competitionDeclined) {
              summary += ' · not competing'
            } else {
              summary += ' · finish pieces, then competition'
            }
            cta = 'View pieces'
          } else {
            cta = 'Add a piece'
          }
          return {
            id: 'showcase',
            icon: '🎭',
            title: 'Showcase pieces',
            summary,
            status,
            statusLabel,
            cta,
            to: '/pieces',
            urgent: musicUrgent,
            nowHint: musicUrgent
              ? this.deadlineNowHint('Showcase music', deadlines.music)
              : null
          }
        })(),
        {
          id: 'guests',
          icon: '🎟️',
          title: 'Guests & meals',
          summary: guestCount
            ? `${guestCount} guest${guestCount === 1 ? '' : 's'} coming along`
            : 'Bring people you love for meals, banquet, or the show',
          status: guestCount ? 'ready' : 'soon',
          statusLabel: guestCount ? 'Registered' : 'Optional',
          cta: guestCount ? 'View guests' : 'Register guests',
          to: '/guests',
          urgent: false,
          nowHint: null
        },
        (() => {
          const mine = (this.photoshootBookings || []).filter(b => b.status === 'booked')
          const first = mine[0]
          if (first && first.editedReady) {
            return {
              id: 'photoshoot',
              icon: '📸',
              title: 'Photoshoot',
              summary: 'Your edited photos are ready to download',
              status: 'ready',
              statusLabel: 'Ready',
              cta: 'Download',
              to: '/photoshoot',
              urgent: false,
              nowHint: null
            }
          }
          if (first && first.proofsSubmitted) {
            return {
              id: 'photoshoot',
              icon: '📸',
              title: 'Photoshoot',
              summary: first.queuePosition
                ? `You’re #${first.queuePosition} in the editing queue`
                : 'Your picks are in the editing queue',
              status: 'ready',
              statusLabel: 'In queue',
              cta: 'View',
              to: '/photoshoot',
              urgent: false,
              nowHint: null
            }
          }
          if (first && first.julieChoose && !first.proofsSubmitted) {
            return {
              id: 'photoshoot',
              icon: '📸',
              title: 'Photoshoot',
              summary: 'Julie is picking your photos',
              status: 'ready',
              statusLabel: 'Julie choosing',
              cta: 'View',
              to: '/photoshoot',
              urgent: false,
              nowHint: null
            }
          }
          if (first && first.proofsReady) {
            return {
              id: 'photoshoot',
              icon: '📸',
              title: 'Photoshoot',
              summary: 'Your proofs are in — pick the ones you’d like edited',
              status: 'attention',
              statusLabel: 'Pick photos',
              cta: 'Choose photos',
              to: '/photoshoot',
              urgent: false,
              nowHint: null
            }
          }
          if (first) {
            return {
              id: 'photoshoot',
              icon: '📸',
              title: 'Photoshoot',
              summary: 'You’re booked — change the time if you need to',
              status: 'ready',
              statusLabel: 'Booked',
              cta: 'View or reschedule',
              to: '/photoshoot',
              urgent: false,
              nowHint: null
            }
          }
          return {
            id: 'photoshoot',
            icon: '📸',
            title: 'Photoshoot',
            summary: 'Fifteen minutes, two edited photos. Book a slot — indoor or outdoor.',
            status: 'soon',
            statusLabel: 'Book a time',
            cta: 'See times',
            to: '/photoshoot',
            urgent: false,
            nowHint: null
          }
        })(),
        (() => {
          const next = this.nextPayment
          const fullyPaid = this.isFullyPaid
          // Auto-charges: soft chip with next amount/date — not "balance due"
          // (that implies action). Overdue or non-schedule extras stay attention.
          let status = 'ready'
          let statusLabel = 'Paid'
          let summary = 'Festival balance is paid — thank you!'
          if (!fullyPaid && next) {
            if (next.overdue) {
              status = 'attention'
              statusLabel = 'Overdue'
              summary = `$${next.amount} was due ${next.dateLabel} — check your card on file`
            } else {
              status = 'soon'
              statusLabel = `Next $${next.amount}`
              summary = `Next auto-charge $${next.amount} on ${next.dateLabel} — no action needed`
            }
          } else if (!fullyPaid && this.totalOutstandingBalance > 0) {
            status = 'attention'
            statusLabel = 'Pay extras'
            summary = `$${this.totalOutstandingBalance} open for guests or costumes`
          } else if (!fullyPaid) {
            status = 'soon'
            statusLabel = 'On schedule'
            summary = 'Automatic monthly payments on the card on file'
          }
          return {
            id: 'billing',
            icon: '💳',
            title: 'Payments',
            summary,
            status,
            statusLabel,
            cta: 'View payments',
            to: '/billing',
            urgent: false,
            nowHint: null
          }
        })()
      ]

      // 2026: hardcoded corps hub. 2027+: generic group pieces dancers can join.
      if (hasCorpsCostumeOrder(this.enrollmentYear)) {
        if (inCorps) {
          const costumePending = this.costumeChecklistItems.some(i => !i.checked)
          cards.splice(5, 0, {
            id: 'corps',
            icon: '🩰',
            title: 'Corps pieces',
            summary: costumePending
              ? 'Your roles · a few costume pieces still to grab'
              : 'Roles, rehearsal videos, and costumes',
            status: costumePending ? 'attention' : 'ready',
            statusLabel: costumePending ? 'A few things left' : 'Your roles',
            cta: 'Open corps hub',
            to: '/my-pieces',
            urgent: costumePending && this.isDeadlineUrgent(deadlines.costume),
            nowHint: costumePending && this.isDeadlineUrgent(deadlines.costume)
              ? this.deadlineNowHint('Costume pieces', deadlines.costume)
              : null
          })
        } else {
          cards.splice(5, 0, {
            id: 'corps',
            icon: '🩰',
            title: 'Corps de Ballet',
            summary: 'Group pieces, roles, and rehearsal videos will show up here once casting is set.',
            status: 'soon',
            statusLabel: 'Coming soon',
            cta: 'Coming soon',
            placeholder: true,
            urgent: false,
            nowHint: null
          })
        }
      } else {
        const mine = (this.groupPiecesForCard || []).filter(p =>
          (p.allDancerIds || []).indexOf(d.id) !== -1
        )
        cards.splice(5, 0, {
          id: 'group-pieces',
          icon: '🩰',
          title: 'Group pieces',
          summary: mine.length
            ? (mine.length === 1 ? 'You’re in 1 group piece' : 'You’re in ' + mine.length + ' group pieces')
            : 'Join a shared dance — rehearsal times land on the itinerary',
          status: mine.length ? 'ready' : 'soon',
          statusLabel: mine.length ? 'Joined' : 'Optional',
          cta: mine.length ? 'View' : 'See pieces',
          to: '/group-pieces',
          urgent: false,
          nowHint: null
        })
      }

      return this.visiblePhotoshootCards(cards)
    },
    /**
     * Now strip: only time-sensitive items.
     * Incomplete prep without a near deadline stays on My festival only.
     * Carpool is never urgent.
     */
    nowItems () {
      return this.festivalCards.filter(c => c.urgent && c.nowHint)
    },
    /** Next rehearsal only belongs in Now if it's soon (within 7 days). */
    showRehearsalInNow () {
      if (!this.nextRehearsalDay || !this.nextRehearsalDay.date) return false
      return this.daysUntilDate(this.nextRehearsalDay.date) <= 7
    },
    hasNowSection () {
      return this.totalOutstandingBalance > 0 ||
        this.nowItems.length > 0 ||
        this.showRehearsalInNow
    },
    travelSummaryLine () {
      const d = this.dancer || {}
      const travelDoc = this.$store.getters.loadedTravelDetails || {}
      if (!lodgingRequiredPrepDone(d, travelDoc)) {
        if (hasTripDetailsOnFile(d, travelDoc)) {
          return 'Trip is in — still a couple of room details'
        }
        return 'Share your flights or drive times when you know them'
      }
      return 'Trip details are in — thank you!'
    },
    dietarySummaryLine () {
      const d = this.dancer || {}
      if (!d.dietaryRestrictionsEntered) {
        return 'So festival meals feel good for you'
      }
      const dietLabels = {
        standard: 'Standard',
        vegetarian: 'Vegetarian',
        vegan: 'Vegan',
        pescetarian: 'Pescetarian'
      }
      const diet = dietLabels[d.diet] || d.diet || 'Saved'
      const glutenLabels = {
        ok: 'gluten fine',
        'prefer-none': 'skip gluten',
        celiac: 'celiac-sensitive'
      }
      const gluten = glutenLabels[d.gluten] || null
      const allergies = Array.isArray(d.foodAllergies) && d.foodAllergies.length
        ? d.foodAllergies.filter(a => a && a !== 'None').join(', ')
        : ''
      const allergyBit = allergies || 'nothing extra to watch'
      return gluten
        ? `${diet} · ${gluten} · ${allergyBit}`
        : `${diet} · ${allergyBit}`
    },
    isInCorps () {
      const d = this.dancer || {}
      if (d.notInCorps) return false
      const keys = ['waltzOfHours', 'aliceInWonderland', 'leJazzHot', 'becoming', 'garlandWaltz']
      if (keys.some(k => d[k] && d[k].participating)) return true
      if (d.costumeOrderCompleted) return true
      return this.costumeChecklistItems.length > 0
    },
    /** Ready resource links (PDFs / videos) under Resources. */
    resourceLinks () {
      const items = [
        {
          id: 'posing-guide',
          icon: '📸',
          title: 'Photoshoot posing guide',
          sub: 'PDF — look your best in photos',
          href: IABF_POSING_GUIDE_PDF_URL
        },
        {
          id: 'travel-tutu',
          icon: '🩰',
          title: 'How to travel with a tutu',
          sub: 'Packing tips for flying with costumes',
          href: 'https://app.brocheballetonline.com/m/mOFdaPHI'
        }
      ]
      if (this.isFaculty) {
        return items.filter(i => FACULTY_GUIDE_IDS.includes(i.id))
      }
      return items
    },
    showResourcesSection () {
      if (this.isFaculty) {
        return this.resourceLinks.length > 0
      }
      return true
    },
    guideSoon () {
      const items = [
        {
          id: 'costume-closet',
          icon: '👗',
          title: 'Costume closet',
          sub: 'Veils, crowns, character pieces to borrow — opens later'
        },
        { id: 'lineup', icon: '🎭', title: 'Showcase lineup', sub: 'Program order — closer to the festival' },
        { id: 'itinerary', icon: '📘', title: 'Itinerary & class locations', sub: 'Daily schedule' },
        { id: 'packing', icon: '🧳', title: 'Packing list', sub: 'What to bring' },
        { id: 'lessons', icon: '📋', title: 'Private lessons & PT', sub: 'Signup opens later' },
        { id: 'merch', icon: '🛍️', title: 'Merch store', sub: 'Free tee + extras when the shop opens' },
        { id: 'recordings', icon: '🎬', title: 'Performance recordings', sub: 'After the festival — solos & corps' }
      ]
      if (!hasCorpsCostumeOrder(this.enrollmentYear)) {
        items.unshift({
          id: 'costume-order',
          icon: '👗',
          title: 'Costume order',
          sub: 'We\'ll open this when corps costumes are ready'
        })
      }
      if (this.isFaculty) {
        return items.filter(i => FACULTY_GUIDE_IDS.includes(i.id))
      }
      return items
    },
    hasMissingMusic () {
      const pieces = this.pieces || []
      // Pending pieces can't upload music yet — only require it after approval
      return pieces.some(p =>
        (p.approvalStatus || 'approved') !== 'pending' &&
        !(p.musicReceived && p.musicUrl)
      )
    },
    corpsPieces () {
      return this.$store.getters.loadedIABFCorpsPiecesForDancer || []
    },
    corpsRecordings () {
      return this.$store.getters.corpsRecordings || {}
    },
    recordingsGoFirst () {
      // Prep stays up top through checkout; recordings move first the day after.
      const departure = festivalWeek(this.enrollmentYear).departureIso
      if (!departure) {
        return false
      }
      const today = new Date().toISOString().slice(0, 10)
      return today > departure
    },
    performanceRecordingRows () {
      // Map corps piece name → the field key the recording is stored
      // under (matches CORPS_INFO in IABFPieces.vue).
      const CORPS_FIELD = {
        'Alice in Wonderland': 'aliceInWonderland',
        'Becoming': 'becoming',
        'Waltz of the Hours': 'waltzOfHours',
        'Le Jazz Hot': 'leJazzHot',
        'Garland Waltz': 'garlandWaltz'
      }
      const out = []
      for (const p of this.pieces || []) {
        out.push({
          key: 'piece:' + p.id,
          name: p.title || '(untitled)',
          tag: p.pieceType || 'Showcase piece',
          url: p.performanceRecordingUrl || ''
        })
      }
      for (const c of this.corpsPieces || []) {
        const field = CORPS_FIELD[c.name] || ''
        out.push({
          key: 'corps:' + (c.id || c.name),
          name: c.name || 'Corps piece',
          tag: 'Corps de ballet',
          url: field ? (this.corpsRecordings[field] || '') : ''
        })
      }
      return out
    },
    costumeChecklistItems () {
      const dancer = this.dancer
      const dancerId = dancer.id
      if (!dancerId) return []
      const items = []
      this.corpsPieces.forEach(piece => {
        (piece.roles || []).forEach(role => {
          if (!role.dancers || !role.dancers.some(d => d.dancerId === dancerId)) return
          ;(role.costumeItems || []).forEach(item => {
            if (!item.purchaseLink) return
            const key = `${piece.id}_${role.id}_${item.id}`
            items.push({
              key,
              description: item.description,
              purchaseLink: item.purchaseLink,
              pieceName: piece.title,
              checked: dancer.costumeChecklist ? (dancer.costumeChecklist[key] ?? false) : false
            })
          })
        })
      })
      return items
    }
  },
  watch: {
    '$route.query.card_updated' (val) {
      if (val === 'true') this.cardUpdateSuccess = true
    },
    async user (newUser) {
      if (newUser && !this.dancerIdentified) {
        try {
          await this.$store.dispatch('loadIABFDancerByUid', newUser.id)
          // If no dancer found by UID, try by email and link the UID
          if (!this.currentIABFDancer && newUser.email) {
            await this.$store.dispatch('linkIABFDancerByEmail', {
              email: newUser.email,
              uid: newUser.id
            })
          }
        } catch (e) {
          console.warn('Dashboard dancer hydrate failed:', e)
        }
      }
    },
    // Reload dancer-specific data whenever the active dancer changes — covers
    // both initial login and admin masquerade switching. immediate: true so
    // it fires when the dashboard mounts with a masquerade already in place.
    'currentIABFDancer.id': {
      immediate: true,
      handler (newId) {
        if (!newId) return
        // Canceled deposit accounts are not full enrollments — skip prep loads
        if (this.currentIABFDancer && this.currentIABFDancer.isCanceledAccount) {
          return
        }
        // Prior-year alumni: history page loads multi-year data; skip prep here
        if (this.isPriorFestivalYear) {
          return
        }
        this.$store.dispatch('loadIABFPiecesForDancer', newId).catch(() => {})
        this.$store.dispatch('loadIABFCorpsPiecesForDancer', newId).catch(() => {})
        if (this.currentIABFDancer && this.currentIABFDancer.email) {
          this.$store.dispatch(
            'loadGuestRegistrationsForDancer',
            (this.currentIABFDancer.email || '').toLowerCase()
          ).catch(() => {})
        }
        this.loadStoreOrders()
        this.loadTravelDetailsForCard()
        if (this.$store.state.masqueradeDancerId) {
          this.$store.dispatch('refreshEffectiveDancer').catch(() => {})
        }
      }
    },
    '$route.hash' () {
      this.scrollToHashResource()
    }
  },
  mounted () {
    this.scrollToHashResource()
  },
  created () {
    if (this.$route.query.embed) {
      this.$store.commit('setLayout', 'embed-layout')
    } else {
      this.$store.commit('setLayout', 'iabf-layout')
    }
    // Live deadline overrides (dietary, carpool, etc.) without redeploy
    this.$store.dispatch('loadIabfDeadlinesConfig', this.$store.getters.iabfDancerYear || null).catch(() => {})
    // 2027 registration gate — canceled-deposit CTA must not show before early access
    this.$store.dispatch('loadRegistrationAccessGate').then((access) => {
      this.registrationAccess = access || { mode: 'closed' }
    }).catch(() => {
      this.registrationAccess = { mode: 'closed' }
    })
    this.$store.dispatch('loadIabfCommunityLinks', this.enrollmentYear).catch(() => {})
    if (this.$route.query.card_updated === 'true') {
      // Card updates now complete on /billing
      this.$router.replace({ path: '/billing', query: { card_updated: 'true' } })
    }
    // Prior-year / canceled: skip current-year prep data loads
    if (this.isCanceledAccount || this.isPriorFestivalYear) {
      return
    }
    // Only show the "Browse the merch store" checklist item once at least one
    // item has been published. Limit query to avoid pulling the whole catalog.
    this.checkStoreHasItems()
    this.loadStoreOrders()
    // Load rehearsal recording URLs admins have attached to each date+piece.
    this.loadRehearsalRecordings()
    // Subscribe to corps performance-recording URLs (one config doc).
    this.$store.dispatch('subscribeCorpsRecordings').then(unsub => {
      this.unsubCorpsRecordings = unsub
    })
    // Check whether the itinerary is published — drives the "Class
    // Locations" tile in Festival prep.
    this.loadItineraryPublished()
    // Pull carpool slots so the airport-carpool tile can derive its done
    // state from actual membership instead of the dancer doc's lagging
    // carpoolResponded flag.
    this.$store.dispatch('loadCarpoolSlots')
    this.loadTravelDetailsForCard()
    this.loadPhotoshootCard()
    this.loadGroupPiecesCard()
  },
  methods: {
    scrollToHashResource () {
      if (this.$route.hash !== '#stage-makeup') {
        return
      }
      this.showMakeup = true
      this.$nextTick(() => {
        const el = document.getElementById('stage-makeup')
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },
    visiblePhotoshootCards (cards) {
      if (this.canSeePhotoshootSchedule) {
        return cards
      }
      return (cards || []).filter((c) => c.id !== 'photoshoot' && c.id !== 'photoshoot-edits')
    },
    async loadGroupPiecesCard () {
      try {
        this.groupPiecesForCard = await this.$store.dispatch('loadGroupPieces', { useAdminYear: false })
      } catch (e) {
        this.groupPiecesForCard = []
      }
    },
    async loadPhotoshootCard () {
      try {
        const config = await this.$store.dispatch('loadPhotoshootConfig', { useAdminYear: false })
        this.photoshootDancerBookingOpen = !!(config && config.dancerBookingOpen)
        if (!this.canSeePhotoshootSchedule) {
          this.photoshootBookings = []
          return
        }
        const rows = await this.$store.dispatch('loadMyPhotoshootBookings')
        this.photoshootBookings = rows || []
      } catch (e) {
        this.photoshootBookings = []
      }
    },
    async loadTravelDetailsForCard () {
      const d = this.currentIABFDancer
      if (!d || !d.id || d.isCanceledAccount || this.isPriorFestivalYear) {
        return
      }
      try {
        if (d.travelDetailsId) {
          await this.$store.dispatch('loadIABFTravelDetails', d.travelDetailsId)
        } else {
          await this.$store.dispatch('loadTravelDetailsByPersonId', d.id)
        }
      } catch (e) {
        console.warn('Dashboard travel details load failed', e)
      }
    },
    async loadItineraryPublished () {
      try {
        const { db } = await import('@/firebase')
        const doc = await db.collection(IABF.config).doc('itinerary').get()
        this.itineraryPublished = !!(doc.exists && doc.data().published)
      } catch (e) {
        // Fail closed — if we can't read the config, hide the Class
        // Locations tile rather than risk linking to an unpublished page.
        this.itineraryPublished = false
      }
    },
    async loadRehearsalRecordings () {
      try {
        const { db } = await import('@/firebase')
        const doc = await db.collection(IABF.config).doc('rehearsal-recordings').get()
        if (doc.exists) {
          const data = doc.data() || {}
          this.rehearsalRecordings = data.recordings || {}
          this.rehearsalSessions = Array.isArray(data.sessions) ? data.sessions : []
        }
      } catch (e) {
        // No big deal — fall back to whatever's hardcoded in the REHEARSALS array.
      }
    },
    async checkStoreHasItems () {
      try {
        const { db } = await import('@/firebase')
        const snap = await db.collection(IABF.storeItems).where('published', '==', true).limit(1).get()
        this.storeHasItems = !snap.empty
      } catch (e) {
        // If the rules / collection aren't set up yet, just leave the item hidden.
        this.storeHasItems = false
      }
    },
    async loadStoreOrders () {
      // Pull this dancer's paid store orders + the store config (deadline)
      // so the merch checklist item can flip to "done" and we can offer to
      // re-open the cart if there's still time.
      try {
        const { db } = await import('@/firebase')
        const email = (this.dancer.email || '').toLowerCase()
        if (!email) return
        const [ordersSnap, cfgSnap] = await Promise.all([
          db.collection(IABF.storeOrders).where('dancerEmail', '==', email).get(),
          db.collection(IABF.config).doc('store').get().catch(() => null)
        ])
        this.storeOrders = ordersSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(o => o.status === 'paid')
          .sort((a, b) => {
            const ad = a.paidAt && a.paidAt.toDate ? a.paidAt.toDate() : new Date(0)
            const bd = b.paidAt && b.paidAt.toDate ? b.paidAt.toDate() : new Date(0)
            return bd - ad
          })
        if (cfgSnap && cfgSnap.exists) {
          this.storeOrderDeadline = (cfgSnap.data() || {}).orderDeadline || ''
        }
      } catch (e) { /* non-fatal */ }
    },
    openStoreOrdersDialog () {
      this.showStoreOrdersDialog = true
    },
    roomLabel (room) {
      return lodgingLabelFromRoom(room)
    },
    /** Days until YYYY-MM-DD (negative if past). */
    daysUntilDate (isoDate) {
      if (!isoDate) return Infinity
      const end = new Date(String(isoDate).slice(0, 10) + 'T23:59:59')
      if (isNaN(end.getTime())) return Infinity
      const now = new Date()
      return Math.ceil((end.getTime() - now.getTime()) / (24 * 60 * 60 * 1000))
    },
    /**
     * Incomplete prep is only "Now" if a real deadline is within 28 days or overdue.
     * No deadline configured → never urgent.
     */
    isDeadlineUrgent (isoDate, windowDays = 28) {
      if (!isoDate) return false
      const days = this.daysUntilDate(isoDate)
      return days <= windowDays
    },
    deadlineNowHint (label, isoDate) {
      const days = this.daysUntilDate(isoDate)
      if (days < 0) return `${label} — deadline passed; please complete ASAP`
      if (days === 0) return `${label} — due today`
      if (days === 1) return `${label} — due tomorrow`
      return `${label} — due in ${days} days`
    },
    onFestivalCardClick (card) {
      if (!card || card.placeholder) return
      if (card.to) {
        this.$router.push(card.to)
      } else if (card.href) {
        window.open(card.href, '_blank', 'noopener')
      }
    },
    onGuideClick (item) {
      if (!item) return
      if (item.to) {
        this.$router.push(item.to)
      } else if (item.href) {
        window.open(item.href, '_blank', 'noopener')
      }
    },
    storeOrderTotalItems (o) {
      return (o.lines || []).reduce((s, l) => s + (l.quantity || 0), 0)
    },
    storeStillOpen () {
      if (!this.storeOrderDeadline) return true
      const today = new Date().toISOString().slice(0, 10)
      return today <= this.storeOrderDeadline
    },
    addToCalendar (block, dayDate, service) {
      const descLines = [`Join Zoom: ${ZOOM_LINK}`]
      if (block.splits && block.splits.length) {
        descLines.push('', 'Group splits:')
        for (const s of block.splits) {
          descLines.push(`${s.displayTime} — ${s.label}`)
        }
      }
      const event = {
        title: `${block.pieceName} Rehearsal — IABF ${this.enrollmentYear}`,
        description: descLines.join('\n'),
        start: block.start,
        end: block.end,
        location: ZOOM_LINK
      }
      let url
      if (service === 'google') url = google(event)
      else if (service === 'outlook') url = outlook(event)
      else if (service === 'yahoo') url = yahoo(event)
      else if (service === 'apple') url = ics(event)
      if (!url) return
      if (service === 'apple') {
        const a = document.createElement('a')
        a.href = url
        a.download = `iabf-${dayDate}-${block.pieceKey}.ics`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      } else {
        window.open(url, '_blank', 'noopener,noreferrer')
      }
    },
    async onCheckEmail () {
      if (!this.loginEmail) return
      this.loginError = ''
      this.checkingEmail = true
      try {
        const methods = await auth.fetchSignInMethodsForEmail(this.loginEmail.trim().toLowerCase())
        if (methods.length > 0) {
          this.loginStep = 'password'
        } else {
          this.loginStep = 'setPassword'
        }
      } catch (e) {
        this.loginError = 'Please enter a valid email address.'
      }
      this.checkingEmail = false
    },
    async onSignIn () {
      if (!this.loginEmail || !this.loginPassword) return
      this.loginError = ''
      this.resetSent = false
      try {
        await this.$store.dispatch('signInIABFDancer', {
          email: this.loginEmail.trim().toLowerCase(),
          password: this.loginPassword
        })
        if (this.currentIABFDancer && this.currentIABFDancer.id) {
          this.$store.dispatch('loadIABFPiecesForDancer', this.currentIABFDancer.id)
          this.$store.dispatch('loadIABFCorpsPiecesForDancer', this.currentIABFDancer.id)
        }
      } catch (e) {
        this.loginError = e.message || 'Sign-in failed. Please try again.'
      }
    },
    async onSignUp () {
      if (!this.loginEmail || !this.loginPassword || !this.confirmPassword) return
      this.loginError = ''
      if (this.loginPassword !== this.confirmPassword) {
        this.loginError = 'Passwords do not match.'
        return
      }
      if (this.loginPassword.length < 6) {
        this.loginError = 'Password must be at least 6 characters.'
        return
      }
      try {
        await this.$store.dispatch('signUpIABFDancer', {
          email: this.loginEmail.trim().toLowerCase(),
          password: this.loginPassword
        })
        if (this.currentIABFDancer && this.currentIABFDancer.id) {
          this.$store.dispatch('loadIABFPiecesForDancer', this.currentIABFDancer.id)
          this.$store.dispatch('loadIABFCorpsPiecesForDancer', this.currentIABFDancer.id)
        }
      } catch (e) {
        this.loginError = e.message || 'Sign-up failed. Please try again.'
      }
    },
    payOutstandingBalance () {
      this.combinedPaymentLoading = true
      const baseUrl = window.location.origin
      const dancerId = this.dancer.id
      const lineItems = []
      if (this.costumeBalanceDue > 0) {
        lineItems.push({ description: 'IABF ' + this.enrollmentYear + ' Costume Balance', amount: this.costumeBalanceDue })
      }
      if (this.guestBalanceDue > 0) {
        lineItems.push({ description: 'IABF ' + this.enrollmentYear + ' Guest Registrations', amount: this.guestBalanceDue })
      }
      this.$store.dispatch('createCombinedCheckoutSession', {
        dancerId,
        guestIds: this.unpaidGuestIds,
        lineItems,
        successUrl: baseUrl + '/iabf/dashboard?payment_success=true',
        cancelUrl: baseUrl + '/iabf/dashboard'
      }).then((checkoutUrl) => {
        window.location.href = checkoutUrl
      }).catch(() => {
        this.combinedPaymentLoading = false
        alert('There was an error creating the payment session. Please try again or contact hello@iabf.dance.')
      })
    },
    updateCard () {
      this.cardUpdateLoading = true
      const baseUrl = window.location.origin
      const dancer = this.dancer
      this.$store.dispatch('createCardSetupSession', {
        dancerId: dancer.id,
        email: dancer.email,
        name: dancer.firstName + ' ' + dancer.lastName,
        successUrl: baseUrl + '/iabf/dashboard?card_updated=true',
        cancelUrl: baseUrl + '/iabf/dashboard'
      }).then((url) => {
        window.location.href = url
      }).catch(() => {
        this.cardUpdateLoading = false
        alert('There was an error. Please try again or contact hello@iabf.dance.')
      })
    },
    async onLogOut () {
      await auth.signOut()
      this.$store.commit('setUser', null)
      this.$store.commit('setCurrentIABFDancer', null)
      this.$store.commit('setLoadedIABFPiecesforDancer', [])
    },
    async onResetPassword () {
      if (!this.loginEmail) {
        this.loginError = 'Please enter your email address first.'
        return
      }
      this.loginError = ''
      try {
        // Go through the Cloud Function so the email is sent via Resend with a
        // brocheballet.com sender — the default Firebase Auth send had poor
        // deliverability (firebaseapp.com noreply often spam-trapped).
        const { functions } = await import('@/firebase')
        const sendReset = functions.httpsCallable('iabfSendPasswordReset')
        await sendReset({ email: this.loginEmail.trim().toLowerCase() })
        this.resetSent = true
      } catch (e) {
        this.loginError = e.message || 'Failed to send reset email. Please try again.'
      }
    },
    onToggleCostumeItem (key, checked) {
      if (!this.dancer || !this.dancer.id) return
      this.$store.dispatch('updateCostumeChecklist', {
        dancerId: this.dancer.id,
        itemKey: key,
        checked
      }).catch((err) => {
        console.error('Costume checklist update failed:', err)
        // Revert optimistic UI if we were mutating local state elsewhere;
        // reload dancer so checkbox matches server.
        this.$store.dispatch('loadIABFDancer', this.dancer.id).catch(() => {})
      })
    }
  }
}
</script>

<style scoped>
.dashboard-page {
  min-height: 100%;
}

.dashboard-container {
  padding-top: 8px !important;
}

.dashboard {
  max-width: 720px;
  margin: 0 auto;
  padding: 28px 16px 64px;
  display: flex;
  flex-direction: column;
  color: var(--iabf-ink, #1c1b1f);
  font-family: var(--iabf-font-body, inherit);
}

.dash-title {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 32px;
  letter-spacing: -.01em;
  line-height: 1.25;
  color: var(--iabf-ink, #1c1b1f);
  margin: 8px 0 8px 0;
}

.dash-heading,
.dashboard h2 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 24px;
  letter-spacing: -.01em;
  line-height: 1.3;
  color: var(--iabf-ink, #1c1b1f);
  margin: 36px 0 14px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--iabf-border, #e3e0e8);
}

.dashboard h4 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 17px;
  color: var(--iabf-ink, #1c1b1f);
  margin: 20px 0 6px 0;
}

.dashboard p,
.dashboard li {
  font-family: var(--iabf-font-body, inherit);
  font-weight: 400;
  font-size: 17px;
  line-height: 1.55;
  color: var(--iabf-ink-muted, #5c5a63);
}

.resources-section { margin: 24px 0 36px; }
.resources-subtitle,
.recordings-subtitle {
  font-size: 15px;
  color: var(--iabf-ink-muted, #5c5a63);
  margin: 0 0 14px;
  font-style: italic;
}
.prior-hero .subtitle {
  margin-bottom: 20px;
}
.alumni-history {
  margin-top: 8px;
}
.prior-history-link {
  margin: 16px 0 0;
}
.prior-history-link a {
  color: var(--iabf-ink-muted, #5c5a63);
  font-weight: 600;
  text-decoration: underline;
}
/* Dedicated CTA — avoid global `a { color: ink }` washing out .iabf-btn text */
.history-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 12px 22px;
  border-radius: 12px;
  background: var(--iabf-primary, #8a957d);
  border: 1.5px solid var(--iabf-primary, #8a957d);
  color: #fff !important;
  font-family: var(--iabf-font-body, inherit);
  font-size: 17px;
  font-weight: 700;
  line-height: 1.2;
  text-decoration: none !important;
  box-shadow: var(--iabf-shadow, none);
  transition: background 0.15s, border-color 0.15s;
}
.history-cta:hover {
  background: var(--iabf-primary-pressed, #6f7a64);
  border-color: var(--iabf-primary-pressed, #6f7a64);
  color: #fff !important;
}
.resources-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
}
.resource-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 16px 18px;
  background: var(--iabf-bg-elevated, #fff);
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 16px;
  text-decoration: none;
  color: inherit;
  box-shadow: var(--iabf-shadow, none);
  transition: transform .12s, box-shadow .12s, border-color .12s;
  min-height: 88px;
}
.resource-card:hover {
  transform: translateY(-1px);
  border-color: var(--iabf-primary, #8a957d);
  box-shadow: 0 4px 16px rgba(138, 149, 125, 0.15);
}
.resource-card-highlight {
  position: relative;
  background: var(--iabf-bg-accent-soft, #eef0eb);
  border: 1.5px solid var(--iabf-primary, #8a957d);
}
.resource-card-highlight:hover {
  border-color: var(--iabf-primary-pressed, #6f7a64);
}
.resource-badge {
  position: absolute;
  top: -10px;
  right: 12px;
  background: var(--iabf-secondary, #8e82a6);
  color: #fff;
  font-family: var(--iabf-font-body, inherit);
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.08em;
  padding: 3px 10px;
  border-radius: 10px;
}
.resource-sub {
  font-size: 12px;
  color: var(--iabf-ink-muted, #5c5a63);
  margin-top: 2px;
  font-style: italic;
}
.resource-icon { font-size: 28px; }
.resource-name {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 16px;
  color: var(--iabf-ink, #1c1b1f);
}
.resource-note {
  font-size: 12px;
  color: var(--iabf-danger, #b54a4a);
  font-style: italic;
  margin-top: 2px;
}

.subtitle {
  font-size: 17px;
  color: var(--iabf-ink-muted, #5c5a63);
  margin-bottom: 8px;
  line-height: 1.45;
}

.costume-balance-banner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
  margin-bottom: 20px;
}

.costume-balance-text {
  flex: 1;
  min-width: 200px;
  font-size: 15px;
  line-height: 1.45;
}

.costume-balance-btn {
  flex-shrink: 0;
}

ul {
  padding-left: 20px;
  margin: 4px 0 12px 0;
}

li {
  margin-bottom: 4px;
}

a {
  color: var(--iabf-ink, #1c1b1f);
  text-decoration: underline;
}

.error-text {
  color: var(--iabf-danger, #b54a4a);
}

.success-text {
  color: var(--iabf-primary, #8a957d);
}

.toggle-link {
  margin-top: 20px;
}

.forgot-link {
  margin-top: 8px;
  font-size: 15px;
}

/* Squarespace-style buttons */
.sq-btn {
  display: inline-block;
  padding: 12px 24px;
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  letter-spacing: .05em;
  text-decoration: none;
  border: 1px solid var(--iabf-primary, #8a957d);
  color: var(--iabf-primary, #8a957d);
  background: transparent;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  margin-right: 12px;
}

.sq-btn:hover {
  background: var(--iabf-primary, #8a957d);
  color: #fff;
}

.sq-btn-primary {
  background: var(--iabf-primary, #8a957d);
  color: #fff;
}

.sq-btn-primary:hover {
  background: var(--iabf-primary-pressed, #6f7a64);
  color: #fff;
}

.welcome-buttons {
  padding: 20px 0 10px 0;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

/* Checklist */
.checklist {
  margin: 8px 0 0 0;
}

.checklist-item {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  border-radius: 14px;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  background: var(--iabf-bg-elevated, #fff);
  margin-bottom: 10px;
  box-shadow: var(--iabf-shadow, none);
}

.checklist-item:last-child {
  border-bottom: none;
}

.checklist-icon {
  margin-right: 14px;
  flex-shrink: 0;
}

.checklist-text {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.checklist-label {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-size: 17px;
  font-weight: 700;
  color: var(--iabf-ink, #1c1b1f);
}

.done-label {
  color: var(--iabf-primary, #8a957d);
}

.checklist-desc {
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  color: var(--iabf-ink-faint, #8a8792);
  line-height: 1.5;
}

.checklist-link {
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  color: var(--iabf-ink, #1c1b1f);
  text-decoration: none;
  white-space: nowrap;
  margin-left: 16px;
  padding: 6px 14px;
  border: 1px solid #ddd;
  transition: background 0.15s;
}

.checklist-link:hover {
  background: #f5f3f0;
}

.checklist-done .checklist-link {
  color: var(--iabf-primary, #8a957d);
  border-color: #c8e6c9;
}

.checklist-link-btn {
  background: transparent;
  cursor: pointer;
  font: inherit;
}

.checklist-clickable {
  cursor: pointer;
  transition: background 0.15s;
}
.checklist-clickable:hover {
  background: #faf8f3;
}

.store-orders-card { padding: 8px 4px 16px; }
.store-orders-title {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 700;
  font-size: 22px;
  word-break: normal;
  padding: 16px 24px 4px;
}
.store-orders-body { padding-top: 4px; }
.store-order-block {
  padding: 12px 0;
  border-bottom: 1px solid #ece9e2;
}
.store-order-block:last-of-type { border-bottom: none; }
.store-order-lines {
  margin: 0 0 8px 0;
  padding: 0 0 0 18px;
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  color: var(--iabf-ink, #1c1b1f);
}
.store-order-lines li { margin-bottom: 2px; }
.store-order-meta {
  font-family: var(--iabf-font-body, inherit);
  font-size: 14px;
  color: #555;
}
.store-orders-hint {
  font-family: var(--iabf-font-body, inherit);
  font-size: 14px;
  font-style: italic;
  color: #555;
  margin: 12px 0 0 0;
}
.store-orders-actions { padding: 8px 20px 16px; }

.checklist-urgent {
  background: #fff3e0;
  border-radius: 6px;
  padding-left: 12px;
  padding-right: 12px;
  margin-left: -12px;
  margin-right: -12px;
}

.checklist-urgent .checklist-label {
  color: #e65100;
}

.checklist-urgent .checklist-link {
  color: #e65100;
  border-color: #ffcc80;
  background: #fff;
}

/* Video embeds */
.video-embed {
  margin: 12px 0 24px 0;
}

.video-embed iframe {
  border-radius: 4px;
}

.costume-balance-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: #fdf6ec;
  border: 1px solid #f0d9b0;
  border-radius: 6px;
  padding: 16px 20px;
  margin-bottom: 32px;
}

.costume-balance-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-family: var(--iabf-font-body, inherit);
  font-size: 16px;
  color: var(--iabf-ink, #1c1b1f);
}

.costume-balance-text strong {
  font-size: 17px;
}

.costume-balance-btn {
  flex-shrink: 0;
}

.info-panels >>> .v-expansion-panel-content__wrap {
  padding: 0 4px 16px 4px;
}

.card-update-section {
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 4px 0 16px 0;
}

.card-update-label {
  font-size: 15px;
  color: var(--iabf-ink-muted, #5c5a63);
  margin: 0;
}

.cancel-policy-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 10px 14px;
  margin-top: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 14px;
  color: #555;
  user-select: none;
}
.cancel-policy-toggle:hover {
  background: #f5f5f5;
}
.cancel-policy-body {
  border: 1px solid #e0e0e0;
  border-top: none;
  border-radius: 0 0 4px 4px;
  padding: 14px 16px;
  font-size: 14px;
  color: #555;
  line-height: 1.6;
}
.cancel-policy-body p {
  margin: 0 0 10px;
}
.cancel-policy-body p:last-child {
  margin-bottom: 0;
}

.payments-bottom {
  order: 99;
}

/* Hub — Now + My festival */
.canceled-deposit-card {
  padding: 28px 24px;
  margin-bottom: 24px;
}
.canceled-deposit-amount {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-size: 48px;
  font-weight: 400;
  letter-spacing: -0.02em;
  margin: 8px 0 12px;
  color: var(--iabf-ink, #1c1b1f);
}
.canceled-deposit-body {
  font-size: 17px;
  line-height: 1.5;
  color: var(--iabf-ink-muted, #5c5a63);
  margin: 0 0 20px;
  max-width: 36em;
}
.canceled-deposit-hint {
  font-size: 14px;
  color: var(--iabf-ink-faint, #8a8792);
  font-style: italic;
  margin: 12px 0 0;
}
.canceled-deposit-footer {
  font-size: 15px;
  color: var(--iabf-ink-muted, #5c5a63);
  margin: 24px 0 0;
  padding-top: 16px;
  border-top: 1px solid var(--iabf-border, #e3e0e8);
}
.canceled-deposit-card .iabf-btn {
  display: inline-block;
  text-decoration: none;
}

.dash-hero {
  margin-bottom: 28px;
}
.guidebook-card {
  margin: -8px 0 28px;
}
.artie-host-card {
  display: flex;
  align-items: center;
  gap: 16px;
  background: var(--iabf-bg-soft, #eef0eb);
  border-radius: 18px;
  padding: 16px 18px;
  margin: 0 0 14px;
}
.artie-host-photo {
  display: block;
  padding: 0;
  border: none;
  background: transparent;
  cursor: zoom-in;
  flex-shrink: 0;
  line-height: 0;
}
.artie-host-photo img {
  width: 112px;
  height: 112px;
  object-fit: contain;
  mix-blend-mode: multiply;
  display: block;
}
.artie-lightbox {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(28, 27, 31, 0.88);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 28px 20px 40px;
}
.artie-lightbox img {
  max-width: min(720px, 100%);
  max-height: calc(100vh - 100px);
  object-fit: contain;
  background: #fff;
  border-radius: 16px;
}
.artie-lightbox-caption {
  margin: 14px 0 0;
  color: #fff;
  font-size: 15px;
  text-align: center;
  max-width: 36em;
}
.artie-lightbox-close {
  position: absolute;
  top: 12px;
  right: 16px;
  border: none;
  background: none;
  color: #fff;
  font-size: 36px;
  line-height: 1;
  cursor: pointer;
}
.artie-host-card .dash-title {
  font-size: 24px;
  margin: 0 0 4px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.artie-mascot-badge {
  display: inline-block;
  font-family: var(--iabf-font-body, inherit);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: var(--iabf-primary, #8a957d);
  color: #fff;
  padding: 3px 8px;
  border-radius: 999px;
  white-space: nowrap;
}
.artie-host-card .subtitle {
  font-size: 15px;
  margin: 0;
}
@media (max-width: 640px) {
  .artie-host-card {
    flex-direction: column;
    align-items: flex-start;
  }
}
.dash-kicker {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--iabf-ink-faint, #8a8792);
  margin: 0 0 8px;
}
.dash-block {
  margin-bottom: 40px;
}
.section-head {
  margin-bottom: 14px;
}
.section-lede {
  font-size: 15px;
  color: var(--iabf-ink-muted, #5c5a63);
  margin: -6px 0 0;
  line-height: 1.45;
}
.checklist-chev {
  color: var(--iabf-ink-faint, #8a8792);
  font-size: 22px;
  line-height: 1;
}

.now-clear-card {
  margin: 0;
}
.now-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
}
.now-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  padding: 14px 16px;
  background: #fff;
  border: 1.5px solid var(--iabf-warning, #c4923a);
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(28, 27, 31, 0.04);
  cursor: pointer;
  font-family: inherit;
}
.now-item-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: var(--iabf-warning, #c4923a);
  flex-shrink: 0;
}
.now-item-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 14px;
  color: var(--iabf-ink-muted, #5c5a63);
}
.now-item-text strong {
  font-size: 16px;
  color: var(--iabf-ink, #1c1b1f);
}
.now-rehearsal {
  margin-bottom: 12px;
}
.now-rehearsal-kicker {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--iabf-ink-faint, #8a8792);
  margin: 0 0 4px;
}
.now-rehearsal-day {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-size: 20px;
  margin: 0 0 10px;
  color: var(--iabf-ink, #1c1b1f);
}
.now-rehearsal-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
  align-items: center;
  font-size: 15px;
  margin-bottom: 6px;
  color: var(--iabf-ink-muted, #5c5a63);
}
.now-rehearsal-row .muted { color: var(--iabf-ink-faint, #8a8792); }

.festival-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 14px;
}
.festival-card {
  background: #fff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(28, 27, 31, 0.04), 0 8px 24px rgba(28, 27, 31, 0.06);
  padding: 18px 18px 16px;
  cursor: pointer;
  transition: border-color 0.12s, transform 0.12s;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 150px;
}
.festival-card:hover {
  transform: translateY(-1px);
  border-color: var(--iabf-primary, #8a957d);
}
.festival-card.is-placeholder {
  cursor: default;
  opacity: 0.88;
}
.festival-card.is-placeholder:hover {
  transform: none;
  border-color: var(--iabf-border, #e3e0e8);
}
.festival-card-cta.is-placeholder-cta {
  font-weight: 600;
  color: var(--iabf-ink-faint, #8a8792);
}
.festival-card.is-attention {
  border-color: rgba(196, 146, 58, 0.55);
}
.festival-card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 4px;
}
.festival-icon { font-size: 26px; line-height: 1; }
.festival-card-title {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 400;
  font-size: 20px;
  margin: 0;
  color: var(--iabf-ink, #1c1b1f);
}
.festival-card-summary {
  font-size: 14px;
  line-height: 1.4;
  color: var(--iabf-ink-muted, #5c5a63);
  margin: 0;
  flex: 1;
}
.festival-card-cta {
  font-size: 14px;
  font-weight: 700;
  color: var(--iabf-primary, #8a957d);
  margin-top: 4px;
}
.status-chip {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 3px 8px;
  border-radius: 999px;
  flex-shrink: 0;
}
.chip-ready {
  background: var(--iabf-bg-soft, #eef0eb);
  color: var(--iabf-primary-pressed, #6f7a64);
}
.chip-attention {
  background: #fff4e0;
  color: #9a6b1a;
}
.chip-soon {
  background: #f0eef4;
  color: var(--iabf-secondary, #8e82a6);
}
.gift-inline {
  margin: 16px 0 0;
  font-size: 15px;
  color: var(--iabf-ink-muted, #5c5a63);
  line-height: 1.45;
}

.resources-stack {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.resource-card--wide {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 14px;
  text-decoration: none;
  color: inherit;
  min-height: auto;
  padding: 18px 20px;
}
.resource-card--wide .resource-name {
  font-size: 17px;
}
.resource-card a,
a.resource-card {
  text-decoration: none;
  color: inherit;
}
.coming-soon-label {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--iabf-ink-faint, #8a8792);
  margin: 0 0 10px;
}
.resource-soon {
  opacity: 0.72;
  cursor: default;
  pointer-events: none;
}
.resource-card.is-link {
  cursor: pointer;
}
.guide-soon .resource-name {
  color: var(--iabf-ink-muted, #5c5a63);
}

.makeup-block {
  background: #fff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(28, 27, 31, 0.04), 0 8px 24px rgba(28, 27, 31, 0.06);
  overflow: hidden;
}
.makeup-toggle {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  text-align: left;
  padding: 18px 20px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: var(--iabf-font-body, inherit);
  color: var(--iabf-ink, #1c1b1f);
}
.makeup-toggle-sub {
  display: block;
  font-size: 13px;
  color: var(--iabf-ink-faint, #8a8792);
  font-weight: 400;
  margin-top: 4px;
}
.makeup-body {
  padding: 0 20px 20px;
  font-size: 16px;
  line-height: 1.55;
  color: var(--iabf-ink-muted, #5c5a63);
}
.makeup-h {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-size: 18px;
  font-weight: 400;
  color: var(--iabf-ink, #1c1b1f);
  margin: 16px 0 8px;
}
.dash-footer {
  margin-top: 8px;
  padding-top: 24px;
  border-top: 1px solid var(--iabf-border, #e3e0e8);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.dash-footer p {
  margin: 0;
  font-size: 15px;
  color: var(--iabf-ink-muted, #5c5a63);
}
.dash-footer a { color: var(--iabf-primary, #8a957d); }
.logout-btn {
  background: transparent;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 12px;
  padding: 10px 16px;
  font-family: var(--iabf-font-body, inherit);
  font-size: 14px;
  color: var(--iabf-ink-muted, #5c5a63);
  cursor: pointer;
}
.logout-btn:hover {
  border-color: var(--iabf-ink-faint, #8a8792);
  color: var(--iabf-ink, #1c1b1f);
}

.card-update-success {
  background: #e8f5e9;
  border: 1px solid #c8e6c9;
  border-radius: 4px;
  padding: 10px 16px;
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  color: var(--iabf-primary, #8a957d);
  margin-bottom: 16px;
}

.costume-subitems {
  margin: 0 0 16px 0;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px 12px;
  background: #fafafa;
}

.costume-subitem {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
}

.costume-checkbox {
  margin-top: 0;
  padding-top: 0;
  flex-shrink: 0;
}

.costume-subitem-text {
  flex-grow: 1;
  font-family: var(--iabf-font-body, inherit);
  font-size: 16px;
  color: var(--iabf-ink, #1c1b1f);
}

/* Rehearsals */
.rehearsals-section {
  margin-top: 32px;
}

.rehearsal-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 8px;
}

.add-calendar-btn {
  display: inline-flex;
  align-items: center;
  font-family: var(--iabf-font-body, inherit);
  font-size: 14px;
  color: var(--iabf-ink, #1c1b1f);
  background: #fff;
  border: 1px solid var(--iabf-primary, #8a957d);
  padding: 5px 12px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.add-calendar-btn:hover {
  background: var(--iabf-primary, #8a957d);
  color: #fff;
}

.add-calendar-btn:hover .v-icon {
  color: #fff !important;
}

.condensed-calendar-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid #ddd;
  padding: 3px 6px;
  cursor: pointer;
  color: var(--iabf-ink-muted, #5c5a63);
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.condensed-calendar-btn:hover {
  background: var(--iabf-primary, #8a957d);
  border-color: var(--iabf-primary, #8a957d);
  color: #fff;
}

.condensed-calendar-btn:hover .v-icon {
  color: #fff !important;
}

.rehearsals-intro {
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  color: #555;
  margin: -8px 0 16px 0;
  line-height: 1.5;
  word-break: break-all;
}

.zoom-inline-link {
  color: var(--iabf-ink, #1c1b1f);
}

.rehearsal-day {
  border: 1px solid var(--iabf-border, #e3e0e8);
  border-radius: 4px;
  background: #faf9f7;
  padding: 14px 18px;
}

.rehearsal-day--featured {
  background: #fff;
  border-color: var(--iabf-primary, #8a957d);
}

.rehearsal-day-label {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 700;
  font-size: 17px;
  color: var(--iabf-ink, #1c1b1f);
  margin-bottom: 10px;
}

.rehearsal-block {
  display: flex;
  flex-direction: column;
  padding: 10px 0;
  border-top: 1px solid #ece8e3;
}

.rehearsal-block:first-of-type {
  border-top: none;
  padding-top: 2px;
}

.rehearsal-block-main {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
}

.rehearsal-piece {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-size: 17px;
  color: var(--iabf-ink, #1c1b1f);
}

.rehearsal-time {
  font-family: var(--iabf-font-body, inherit);
  font-size: 16px;
  color: var(--iabf-ink-muted, #5c5a63);
}

.rehearsal-splits {
  list-style: none;
  padding: 6px 0 0 0;
  margin: 0;
}

.rehearsal-splits li {
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  color: var(--iabf-ink-muted, #5c5a63);
  line-height: 1.6;
  margin: 0;
}

.rehearsal-join {
  display: inline-flex;
  align-items: center;
  font-family: var(--iabf-font-body, inherit);
  font-size: 14px;
  color: var(--iabf-ink, #1c1b1f);
  text-decoration: none;
  padding: 5px 12px;
  border: 1px solid var(--iabf-primary, #8a957d);
  background: #fff;
  transition: background 0.15s, color 0.15s;
}

.rehearsal-join:hover {
  background: var(--iabf-primary, #8a957d);
  color: #fff;
}

.rehearsals-condensed {
  margin-top: 18px;
}

.rehearsals-condensed-label {
  font-family: var(--iabf-font-body, inherit);
  font-size: 13px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--iabf-ink-faint, #8a8792);
  margin-bottom: 8px;
}

.condensed-day {
  padding: 8px 0;
  border-top: 1px solid #ece8e3;
}

.condensed-day:first-of-type {
  border-top: none;
}

.condensed-day-label {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 700;
  font-size: 15px;
  color: var(--iabf-ink, #1c1b1f);
  margin-bottom: 2px;
}

.condensed-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  color: #555;
  line-height: 1.7;
}

.condensed-piece {
  color: var(--iabf-ink, #1c1b1f);
}

.condensed-time {
  color: var(--iabf-ink-muted, #5c5a63);
  white-space: nowrap;
}

.condensed-splits {
  list-style: none;
  margin: 2px 0 6px 0;
  padding: 0;
}

.condensed-splits li {
  font-family: var(--iabf-font-body, inherit);
  font-size: 14px;
  color: var(--iabf-ink-faint, #8a8792);
  line-height: 1.6;
}

.condensed-meta {
  display: flex;
  align-items: baseline;
  gap: 12px;
  white-space: nowrap;
}

.condensed-recording {
  font-family: var(--iabf-font-body, inherit);
  font-size: 14px;
  color: var(--iabf-primary, #8a957d);
  text-decoration: none;
  padding: 4px 10px;
  border: 1px solid var(--iabf-primary, #8a957d);
  background: #fff;
  transition: background 0.15s, color 0.15s;
}

.condensed-recording:hover {
  background: var(--iabf-primary, #8a957d);
  color: #fff;
}

.condensed-recording-pending {
  font-family: var(--iabf-font-body, inherit);
  font-size: 14px;
  color: var(--iabf-ink-faint, #8a8792);
  font-style: italic;
}
</style>
